/**
 * Moodboard Studio frontend controller
 * Style-picker + free-text description → AI-generated moodboard + products
 */
(() => {
  const API_BASE = (() => {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local')) {
      return 'http://localhost:3007';
    }
    return 'https://ai.sanizone.nl';
  })();

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  const form = $('#moodboard-form');
  const stylePicker = $('#moodboard-style-picker');
  const description = $('#moodboard-description');
  const charCount = $('#moodboard-charcount');
  const generateBtn = $('#moodboard-generate');
  const resultsMount = $('#moodboard-results');

  if (!form) return;

  let selectedStyle = 'japandi';

  // Update character count
  if (description) {
    description.addEventListener('input', (e) => {
      const len = e.target.value.length;
      charCount.textContent = `${len} / 200`;
      generateBtn.disabled = len === 0;
    });
  }

  // Style picker
  if (stylePicker) {
    stylePicker.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-style]');
      if (!btn) return;
      selectedStyle = btn.dataset.style;
      activateStyleBtn(selectedStyle);
      generateBtn.disabled = !description.value.trim();
    });
  }

  function activateStyleBtn(style) {
    if (!stylePicker) return;
    $$('[data-style]', stylePicker).forEach((b) => {
      const active = b.dataset.style === style;
      b.classList.toggle('bg-ink', active);
      b.classList.toggle('text-bone', active);
      b.classList.toggle('bg-bone', !active);
      b.classList.toggle('text-ink', !active);
    });
  }

  async function generate() {
    if (!description.value.trim()) {
      alert('Beschrijf je droombadkamer alstublieft.');
      return;
    }

    generateBtn.disabled = true;
    generateBtn.textContent = 'AI aan het werk…';

    resultsMount.innerHTML = skeletonMarkup();
    resultsMount.scrollIntoView({ behavior: 'smooth', block: 'start' });

    try {
      // Step 1: Get preferences from description
      const suggestRes = await fetch(`${API_BASE}/api/moodboard-suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          style: selectedStyle,
          description: description.value,
        }),
      });

      if (!suggestRes.ok) {
        throw new Error(`Suggest API error: ${suggestRes.status}`);
      }

      const suggest = await suggestRes.json();

      // Step 2: Fetch products based on preferences
      const productRes = await fetch(`${API_BASE}/api/moodboard-products?style=${encodeURIComponent(suggest.style)}&budget=${suggest.budget}`);

      if (!productRes.ok) {
        throw new Error(`Products API error: ${productRes.status}`);
      }

      const productData = await productRes.json();

      resultsMount.innerHTML = resultsMarkup(suggest, productData, API_BASE);
      resultsMount.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      console.error(err);
      resultsMount.innerHTML = errorMarkup('Kon het moodboard niet genereren. Probeer het opnieuw.');
    } finally {
      generateBtn.disabled = false;
      generateBtn.textContent = 'Genereer moodboard';
    }
  }

  if (generateBtn) {
    generateBtn.addEventListener('click', generate);
  }

  function skeletonMarkup() {
    const sectionStyle = 'background: var(--bone); color: var(--ink); padding: 96px 0;';
    const inner = 'max-width: 1280px; margin: 0 auto; padding: 0 24px;';
    const label = 'font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin-bottom: 14px;';
    const bar = 'height: 6px; background: rgba(26,26,26,0.08); border-radius: 999px; overflow: hidden; margin-top: 24px;';
    const fill = 'height: 100%; background: var(--moss); width: 15%; transition: width 0.6s cubic-bezier(.22,.61,.36,1);';
    const card = `
      <div style="position: relative; border-radius: 20px; overflow: hidden; background: var(--paper); aspect-ratio: 1/1;">
        <div style="position: absolute; inset: 0; background: linear-gradient(90deg, rgba(201,168,123,0) 0%, rgba(201,168,123,0.18) 50%, rgba(201,168,123,0) 100%); background-size: 200% 100%; animation: sh-skel 1.8s ease-in-out infinite;"></div>
      </div>`;

    return `
      <section style="${sectionStyle}">
        <div style="${inner}">
          <p id="moodboard-progress-label" style="font-family: 'Inter', ui-sans-serif, system-ui; font-weight: 600; font-size: 20px; line-height: 1.2; letter-spacing: -0.02em; color: var(--ink); margin: 0 0 20px;">AI stylt je moodboard…</p>
          <div style="${bar}">
            <div id="moodboard-progress-bar-fill" style="${fill}"></div>
          </div>
          <p style="font-size: 13px; color: var(--muted); margin-top: 14px;">Dit duurt meestal 10 tot 15 seconden.</p>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 24px; margin-top: 48px;">${card}${card}${card}</div>
        </div>
      </section>
      <style>
        @keyframes sh-skel { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      </style>
    `;
  }

  function errorMarkup(msg) {
    return `
      <section style="background: var(--bone); color: var(--ink); padding: 96px 0;">
        <div style="max-width: 720px; margin: 0 auto; padding: 0 24px; text-align: center;">
          <p style="font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin-bottom: 12px;">Helaas</p>
          <h3 style="font-family: 'Inter', ui-sans-serif, system-ui; font-weight: 600; font-size: 24px; letter-spacing: -0.02em; color: var(--ink); margin: 0 0 12px;">${escapeHtml(msg)}</h3>
          <p style="font-size: 15px; color: var(--muted); margin: 0;">Stuur je idee direct naar Joël via email.</p>
        </div>
      </section>`;
  }

  function resultsMarkup(suggest, productData, base) {
    const { style, budget, roomsize } = suggest;
    const { products, bundle } = productData;

    const styleLabel = {
      japandi: 'Japandi',
      'warm-modern': 'Warm Modern',
      spa: 'Spa & Wellness',
      biophilic: 'Biophilic',
      minimalist: 'Minimalistisch',
      classic: 'Klassiek',
    }[style] || style;

    const ctaStyle = 'display: inline-flex; align-items: center; gap: 10px; background: var(--ink); color: var(--bone); padding: 16px 28px; border-radius: 999px; font-size: 15px; text-decoration: none; transition: background 0.3s ease, transform 0.2s ease;';
    const bundleHref = bundle?.resolved ? bundle.cartUrl : '#moodboard-products';
    const bundleLabel = bundle?.resolved ? `Voeg de hele set toe · €${fmt(bundle.total)}` : `Bekijk de producten · €${fmt(bundle.total)}`;

    const productCards = (products || [])
      .map(
        (p) => `
      <a href="${p.url}" style="display: block; text-decoration: none; color: inherit;">
        <div style="position: relative; border-radius: 16px; overflow: hidden; background: var(--paper); aspect-ratio: 1/1;">
          <img src="${p.image}" style="width: 100%; height: 100%; object-fit: cover; display: block;" alt="${escapeHtml(p.title)}" loading="lazy" />
        </div>
        <p style="font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin: 12px 0 4px;">${escapeHtml(p.category)}</p>
        <p style="font-size: 14px; line-height: 1.4; color: var(--ink); margin: 0 0 4px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">${escapeHtml(p.title)}</p>
        <p style="font-size: 14px; font-weight: 500; color: var(--ink); margin: 0;">€${fmt(p.price)}</p>
      </a>
    `
      )
      .join('');

    return `
      <section style="background: var(--bone); color: var(--ink); padding: 96px 0 112px;">
        <div style="max-width: 1280px; margin: 0 auto; padding: 0 24px;">
          <p style="font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin: 0 0 14px;">Stijl · ${escapeHtml(styleLabel)}</p>
          <h2 style="font-family: 'Inter', ui-sans-serif, system-ui; font-weight: 600; font-size: clamp(40px, 6vw, 64px); line-height: 1.05; letter-spacing: -0.03em; color: var(--ink); margin: 0;">Je moodboard.</h2>
          <p style="font-size: 16px; color: var(--ink-soft); margin: 16px 0 0;">Budget: €${budget} · Ruimte: ${roomsize}m²</p>
        </div>
      </section>

      <section id="moodboard-products" style="background: var(--cream); color: var(--ink); padding: 96px 0;">
        <div style="max-width: 1280px; margin: 0 auto; padding: 0 24px;">
          <div style="display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 44px;">
            <div>
              <p style="font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin: 0 0 10px;">Producten</p>
              <h3 style="font-family: 'Inter', ui-sans-serif, system-ui; font-weight: 600; font-size: clamp(28px, 4vw, 44px); line-height: 1.1; letter-spacing: -0.02em; color: var(--ink); margin: 0;">${(products || []).length} items, samengesteld.</h3>
            </div>
            <a href="${bundleHref}" style="${ctaStyle}" onmouseover="this.style.background='var(--moss)'" onmouseout="this.style.background='var(--ink)'">${bundleLabel}</a>
          </div>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 24px;">${productCards}</div>
        </div>
      </section>
    `;
  }

  function fmt(n) {
    return Number(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  activateStyleBtn('japandi');
})();
